import { Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { ChecklistTask } from "../../../api/types/objectTypes";
import FieldEditRow from "../editors/FieldEditRow";
import { getFieldValues, getInitialFieldsEditState, updateFieldValueAction } from "../editors/fieldsEditState";
import ObjectTasksContext from "./ObjectTasksContext";

interface Props {
  task: ChecklistTask;
}

const updateChecklistTaskDetails = withErrorHandling(adminApi.updateChecklistTaskDetails);

const ObjectTaskDetailsEditForm = ({ task }: Props) => {
  const {
    objectType,
    objectId,
    editableChecklistTaskFields,
    hasPermissionsToManageObjectData,
    onChecklistTasksUpdate,
  } = useDefinedContext(ObjectTasksContext, "ObjectTasksContext");

  const { sendNotificationError } = useNotificationContext();

  const [fieldsState, setFieldsState] = useState(() =>
    getInitialFieldsEditState(editableChecklistTaskFields, task.fieldValues)
  );
  const [description, setDescription] = useState(task.description ?? "");
  const [saving, setSaving] = useState(false);

  const readOnly = !hasPermissionsToManageObjectData;
  const isDirty = fieldsState.isDirty || description !== (task.description ?? "");

  const handleFieldChange = (fieldId: string, value: unknown) => {
    setFieldsState(updateFieldValueAction(fieldId, value));
  };

  const handleCancel = () => {
    setFieldsState(getInitialFieldsEditState(editableChecklistTaskFields, task.fieldValues));
    setDescription(task.description ?? "");
  };

  const handleSave = async () => {
    setSaving(true);
    const [updatedTask, error] = await updateChecklistTaskDetails(objectType, objectId, {
      taskId: task.id,
      descriptionUserInput: description,
      fieldValuesUserInput: getFieldValues(fieldsState),
    });
    setSaving(false);

    if (error) {
      logError(error, "[ObjectTaskDetailsEditForm] updateChecklistTaskDetails");
      sendNotificationError(error.message);
      return;
    }

    setFieldsState(getInitialFieldsEditState(editableChecklistTaskFields, updatedTask.fieldValues));
    setDescription(updatedTask.description ?? "");
    onChecklistTasksUpdate([updatedTask]);
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="subtitle1">Description</Typography>
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={readOnly}
        />
      </Stack>
      {fieldsState.fields.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle1">Fields</Typography>
          <Stack spacing={1.5}>
            {fieldsState.fields.map(({ field, value, validationError }) => (
              <FieldEditRow
                key={field.id}
                field={field}
                value={value}
                validationError={validationError}
                onChange={handleFieldChange}
                disabled={readOnly}
              />
            ))}
          </Stack>
        </Stack>
      )}
      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-start", pt: 1 }}>
          <Button variant="contained" onClick={handleSave} loading={saving} disabled={!isDirty || !fieldsState.isValid}>
            Save
          </Button>
          <Button color="secondary" onClick={handleCancel} disabled={saving || !isDirty}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default ObjectTaskDetailsEditForm;
